// @flow
// Окно загрузки лекал папкой. Показывает, что будет загружено, спрашивает про
// замену лекал, которые уже есть, а при загрузке в бренд — название и тип
// новой модели (по умолчанию название папки).
import React, { useEffect, useMemo, useState } from 'react';
import _countBy from 'lodash/countBy';
import _maxBy from 'lodash/maxBy';
import { decodeException } from '../elements/LoadFailed';
import { nameKey } from './folderUpload';
import {
  createDevice,
  listDeviceTypeOptions,
  listDeviceVariants,
  uploadLekalo,
} from './folderUpload.client';

type Props = {
  mode: 'device' | 'brand',
  brand: Object,
  device?: Object,        // модель, в которую грузим (mode = device)
  existing?: Array<Object>, // её лекала
  devices?: Array<Object>,  // модели бренда (mode = brand)
  folderName?: string,      // папка модели (mode = brand)
  plan: { items: Array<Object>, warnings: Array<string> },
  onClose: (changed: boolean) => void,
};

function Thumb({ file }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <img src={url} alt="" style={{ maxWidth: 48, maxHeight: 48 }} />;
}

// Самый частый тип моделей бренда, иначе «Смартфон»
function defaultDeviceType(devices, deviceTypes) {
  const counts = _countBy((devices || []).filter(d => d.deviceType), d => d.deviceType.id);
  const top = _maxBy(Object.keys(counts), id => counts[id]);
  if (top && deviceTypes.some(t => t.id === top)) return top;
  const phone = deviceTypes.find(t => nameKey(t.name) === 'смартфон');
  return phone ? phone.id : (deviceTypes[0] ? deviceTypes[0].id : '');
}

export default function FolderUploadDialog({
  mode,
  brand,
  device,
  existing,
  devices,
  folderName,
  plan,
  onClose,
}: Props) {
  const isBrand = mode === 'brand';
  const [modelName, setModelName] = useState(folderName || '');
  const [deviceTypes, setDeviceTypes] = useState(null);
  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [modelVariants, setModelVariants] = useState(isBrand ? [] : (existing || []));
  const [checking, setChecking] = useState(false);
  const [replace, setReplace] = useState({});
  const [phase, setPhase] = useState('review');
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Модель бренда с таким же названием: лекала пойдут в неё
  const sameModel = isBrand
    ? (devices || []).find(d => nameKey(d.name) === nameKey(modelName))
    : null;
  const sameModelId = sameModel ? sameModel.id : null;

  // Как у модальных окон Bootstrap: страница под окном не прокручивается
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  useEffect(() => {
    if (!isBrand) return;
    listDeviceTypeOptions()
      .then(types => {
        setDeviceTypes(types);
        setDeviceTypeId(defaultDeviceType(devices, types));
      })
      .catch(e => setLoadError(decodeException(e)));
  }, []);

  useEffect(() => {
    if (!isBrand) return undefined;
    if (!sameModelId) {
      setModelVariants([]);
      return undefined;
    }
    let alive = true;
    setChecking(true);
    listDeviceVariants(sameModelId)
      .then(list => { if (alive) setModelVariants(list); })
      .catch(e => { if (alive) setLoadError(decodeException(e)); })
      .then(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [sameModelId]);

  const existingByName = useMemo(() => {
    const map = new Map();
    modelVariants.forEach(v => map.set(nameKey(v.name), v));
    return map;
  }, [modelVariants]);

  const conflicts = plan.items.filter(item => existingByName.has(nameKey(item.name)));
  const willReplace = conflicts.filter(item => replace[nameKey(item.name)]);
  const queue = plan.items.filter(item => !existingByName.has(nameKey(item.name)) || replace[nameKey(item.name)]);
  const allReplaced = conflicts.length > 0 && willReplace.length === conflicts.length;

  const nameMissing = isBrand && !modelName.trim();
  const typeMissing = isBrand && !sameModel && !deviceTypeId;
  const canStart = phase === 'review' && queue.length > 0 && !nameMissing && !typeMissing
    && !checking && !loadError && (!isBrand || deviceTypes);

  function toggleAll() {
    const next = {};
    if (!allReplaced) conflicts.forEach(item => { next[nameKey(item.name)] = true; });
    setReplace(next);
  }

  async function start() {
    setPhase('uploading');
    let deviceId = isBrand ? sameModelId : device.id;
    let modelCreated = false;
    if (!deviceId) {
      try {
        const created = await createDevice({ brandId: brand.id, deviceTypeId, name: modelName.trim() });
        deviceId = created.id;
        modelCreated = true;
      } catch (e) {
        setResult({ error: `Модель не создана: ${decodeException(e)}` });
        setPhase('done');
        return;
      }
    }

    let added = 0;
    let replaced = 0;
    const failed = [];
    for (let i = 0; i < queue.length; i += 1) {
      const item = queue[i];
      setProgress({ done: i, total: queue.length, current: item.name });
      const same = existingByName.get(nameKey(item.name));
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadLekalo(deviceId, item, same ? same.id : null);
        if (same) replaced += 1; else added += 1;
      } catch (e) {
        failed.push({ name: item.name, message: decodeException(e) });
      }
    }
    setResult({
      added,
      replaced,
      skipped: plan.items.length - queue.length,
      failed,
      modelCreated,
    });
    setPhase('done');
  }

  const changed = Boolean(result && (result.added || result.replaced || result.modelCreated));
  const title = isBrand
    ? `Модель из папки «${folderName}»`
    : `Загрузка лекал в модель ${device.name}`;

  return (
    <>
      <div className="modal fade show d-block folder-upload" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
            </div>
            <div className="modal-body">
              {loadError && <div className="alert alert-danger">{loadError}</div>}

              {phase === 'review' && isBrand && (
                <div className="mb-3">
                  <div className="form-group">
                    <label htmlFor="folder-upload-name">Название модели</label>
                    <input
                      id="folder-upload-name"
                      className="form-control"
                      value={modelName}
                      onChange={e => setModelName(e.target.value)}
                    />
                    <small className="form-text text-muted">
                      По умолчанию это название папки. Можно оставить или написать своё.
                    </small>
                  </div>
                  {sameModel ? (
                    <div className="alert alert-info mb-0">
                      Модель «{sameModel.name}» уже есть в бренде {brand.name}. Лекала добавятся в неё.
                    </div>
                  ) : (
                    <div className="form-group mb-0">
                      <label htmlFor="folder-upload-type">Тип устройства</label>
                      <select
                        id="folder-upload-type"
                        className="form-control"
                        value={deviceTypeId}
                        onChange={e => setDeviceTypeId(e.target.value)}
                        disabled={!deviceTypes}
                      >
                        {(deviceTypes || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {phase === 'review' && (
                <>
                  {conflicts.length > 0 && (
                    <div className="alert alert-warning">
                      Уже есть в модели: {conflicts.length}. Отметьте, какие заменить. Неотмеченные останутся как есть.
                      {conflicts.length > 1 && (
                        <div className="form-check mt-1">
                          <input
                            id="folder-upload-all"
                            type="checkbox"
                            className="form-check-input"
                            checked={allReplaced}
                            onChange={toggleAll}
                          />
                          <label className="form-check-label" htmlFor="folder-upload-all">Заменить все</label>
                        </div>
                      )}
                    </div>
                  )}
                  {checking && <p className="text-muted">Проверяю, какие лекала уже есть в модели…</p>}
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Лекало</th>
                        <th>Картинка</th>
                        <th>Папка</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {plan.items.map(item => {
                        const key = nameKey(item.name);
                        const same = existingByName.has(key);
                        return (
                          <tr key={key} className="folder-upload__item">
                            <td className="align-middle">{item.name}</td>
                            <td className="align-middle">
                              {item.preview ? <Thumb file={item.preview} /> : <span className="text-muted">нет</span>}
                            </td>
                            <td className="align-middle text-muted small">{item.dir}</td>
                            <td className="align-middle text-nowrap">
                              {same ? (
                                <div className="form-check">
                                  <input
                                    id={`folder-upload-${key}`}
                                    type="checkbox"
                                    className="form-check-input folder-upload__replace"
                                    checked={Boolean(replace[key])}
                                    onChange={e => setReplace({ ...replace, [key]: e.target.checked })}
                                  />
                                  <label className="form-check-label" htmlFor={`folder-upload-${key}`}>
                                    уже есть, заменить
                                  </label>
                                </div>
                              ) : <span className="text-success">новое</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {plan.warnings.length > 0 && (
                    <div className="alert alert-secondary small mb-0">
                      {plan.warnings.map((w, i) => <div key={i}>{w}</div>)}
                    </div>
                  )}
                </>
              )}

              {phase === 'uploading' && (
                <div className="folder-upload__progress">
                  <p>Загружаю {progress.done + 1} из {progress.total}: {progress.current}</p>
                  <div className="progress">
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {phase === 'done' && result && (
                <div className="folder-upload__result">
                  {result.error ? (
                    <div className="alert alert-danger">{result.error}</div>
                  ) : (
                    <p>
                      Готово{result.modelCreated ? `, модель «${modelName.trim()}» создана` : ''}.
                      Добавлено лекал: {result.added}, заменено: {result.replaced}
                      {result.skipped ? `, оставлено как было: ${result.skipped}` : ''}.
                    </p>
                  )}
                  {result.failed && result.failed.length > 0 && (
                    <div className="alert alert-danger">
                      Не загрузились:
                      {result.failed.map(f => <div key={f.name}>{f.name}: {f.message}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {phase === 'review' && (
                <>
                  <button type="button" className="btn btn-primary" disabled={!canStart} onClick={start}>
                    Загрузить ({queue.length})
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>
                    Отмена
                  </button>
                </>
              )}
              {phase === 'done' && (
                <button type="button" className="btn btn-primary" onClick={() => onClose(changed)}>
                  Закрыть
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}
